import OrdenDatos from './OrdenDatos';
import ClienteDatos from './ClienteDatos';
import VehiculoDatos from './VehiculoDatos';
import DescripcionFalla from './DescripcionFalla';
import RecepcionVehiculo from './RecepcionVehiculo';
import AutorizacionFirma from './AutorizacionFirma';
import React, { useState, useRef, useEffect } from 'react';
import { useApi } from '../../api';
import { toPng } from 'html-to-image';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaDollarSign } from 'react-icons/fa';
import { formatearFechaLocal, parsearFechaLocal } from '../utils';
import { Alert, Box, Button, CircularProgress, Container, TextField, Typography } from '@mui/material';


const RECEPCION_ITEMS = [
  "Espejo Izquierdo", "Espejo Derecho", "Vidrios", "Radio",
  "Pantalla", "Encendedor", "Antena", "Cont. de Puertas", "Cargador Cel.", "Triángulos",
  "Cubresol", "Herramientas", "Gato", "Llanta de Refacción", "Faros/Luces",
  "Tapa de Gasolina", "Placas", "Tapetes", "Extintor", "Llave de Tuercas"
];

const NuevaOrden = () => {
  const sigPadRef = useRef();
  const esquemaRef = useRef();
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState(null);
  const [emailAdvertencia, setEmailAdvertencia] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const presupuestoOrigen = location.state?.presupuesto || null;
  const hoy = new Date();
  const manana = new Date(hoy);
  manana.setDate(hoy.getDate() + 1);
  const api = useApi();


  const [form, setForm] = useState({
    cliente: '',
    cliente_id: '',
    direccion: '',
    telefono: '',
    email: '',
    vehiculo: {
      placa: '',
      marca: '',
      modelo: '',
      color: '',
      linea: ''
    },
    abono: 0,
    fecha_recepcion: formatearFechaLocal(hoy),
    fecha_entrega: formatearFechaLocal(manana),
    descripcion: '',
    observaciones: '',
    recepcion: {},
    kilometraje: '',
    gasolina: 50,
    presupuesto: false,
    marcas: [],
    autoriza_reparacion: false,
    autoriza_prueba: false,
    firma_cliente: '',
  });

const handleChange = (e) => {
  const { name, value, type, checked } = e.target;

  // Caso especial: checkbox
  if (type === 'checkbox') {
    setForm(prev => ({ ...prev, [name]: checked }));
    return;
  }

  // Caso especial: si cambia la fecha de recepción
  if (name === 'fecha_recepcion') {
    const nuevaFecha = parsearFechaLocal(value);
    const fechaSiguiente = new Date(nuevaFecha);
    fechaSiguiente.setDate(nuevaFecha.getDate() + 1);

    const fechaEntregaActual = form.fecha_entrega ? parsearFechaLocal(form.fecha_entrega) : null;
    const nuevaEntregaISO = formatearFechaLocal(fechaSiguiente);

    setForm(prev => ({
      ...prev,
      [name]: value,
      fecha_entrega:
        !fechaEntregaActual || fechaEntregaActual <= nuevaFecha
          ? nuevaEntregaISO
          : prev.fecha_entrega
    }));
    return;
  }

  // Caso general para cualquier otro campo
  setForm(prev => ({ ...prev, [name]: value }));
};


  const handleMarkerChange = (data) => {
    if (data === 'clear') {
      setForm(prev => ({ ...prev, marcas: [] }));
    } else if (data === 'removeLast') {
      setForm(prev => ({
        ...prev,
        marcas: prev.marcas.slice(0, -1)
      }));
    } else {
      setForm(prev => ({
        ...prev,
        marcas: [...(prev.marcas || []), data]
      }));
    }
  };

  const handleRecepcionCheck = (e) => {
    const { name, checked } = e.target;
    setForm(prev => ({
      ...prev,
      recepcion: {
        ...prev.recepcion,
        [name]: checked
      }
    }));
  };

const handleSubmit = async () => {
  if (loading) return; // evita doble click rápido
  setLoading(true);

  let firmaData = '';
  if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
    firmaData = sigPadRef.current.getCanvas().toDataURL('image/png');
  }

  let esquemaImagen = '';
  if (esquemaRef.current) {
    try {
      esquemaImagen = await toPng(esquemaRef.current);
    } catch (err) {
      console.error('❌ Error capturando el esquema:', err);
    }
  }

  const payload = {
    cliente: form.cliente,
    direccion: form.direccion,
    telefono: form.telefono,
    email: form.email,
    vehiculo: form.vehiculo.placa,
    fecha_recepcion: form.fecha_recepcion,
    fecha_entrega: form.fecha_entrega,
    descripcion: form.descripcion,
    observaciones: form.observaciones,
    recepcion: form.recepcion,
    kilometraje: form.kilometraje,
    gasolina: form.gasolina,
    presupuesto: form.presupuesto,
    abono: form.abono,
    autoriza_reparacion: form.autoriza_reparacion,
    autoriza_prueba: form.autoriza_prueba,
    marcas: form.marcas,
    firma: firmaData,
    esquema: esquemaImagen
  };

  setFormErrors(null);
  setEmailAdvertencia(null);
  try {
    const response = await api.post(
      `${process.env.REACT_APP_API_URL}/api/ordenes/`,
      payload
    );

    if (presupuestoOrigen) {
      try {
        await api.patch(
          `${process.env.REACT_APP_API_URL}/api/presupuestos/${presupuestoOrigen.id}/`,
          { orden_generada: true }
        );
      } catch (err) {
        console.error('No se pudo marcar el presupuesto como convertido:', err);
      }
    }

    if (response.data.email_advertencia) {
      setEmailAdvertencia(response.data.email_advertencia);
      setLoading(false);
      return;
    }
    navigate('/ordenes');
  } catch (error) {
    console.error('Error al guardar la orden:', error);
    const data = error.response?.data;
    if (data && typeof data === 'object') {
      const mensajes = Object.entries(data).flatMap(([campo, errores]) => {
        const nombreCampo = campo === 'non_field_errors' ? 'General' : campo;
        const lista = Array.isArray(errores) ? errores : [errores];
        return lista.map(msg => `• ${nombreCampo}: ${msg}`);
      });
      setFormErrors(mensajes);
    } else {
      setFormErrors(['❌ Error inesperado al guardar la orden. Intente de nuevo.']);
    }
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  } finally {
    setLoading(false);
  }
};
useEffect(() => {
  const inicial = {};
  RECEPCION_ITEMS.forEach(item => {
    inicial[item] = true;
  });
  setForm(prev => ({ ...prev, recepcion: inicial }));
}, []);

// Si se llega desde un presupuesto ("Elevar a Orden de Servicio"), precargamos sus datos
useEffect(() => {
  if (!presupuestoOrigen) return;

  setForm(prev => ({
    ...prev,
    cliente: presupuestoOrigen.cliente || '',
    telefono: presupuestoOrigen.telefono || '',
    email: presupuestoOrigen.email || '',
    vehiculo: { ...prev.vehiculo, placa: presupuestoOrigen.vehiculo || '' },
    descripcion: presupuestoOrigen.descripcion || '',
    observaciones: `Generado desde presupuesto #${presupuestoOrigen.id}`,
  }));

  // Buscamos el cliente_id/dirección para habilitar el filtro de vehículos por cliente
  api.get(`${process.env.REACT_APP_API_URL}/api/clientes/`)
    .then(res => {
      const match = res.data.find(c => c.nombre === presupuestoOrigen.cliente);
      if (match) {
        setForm(prev => ({ ...prev, cliente_id: match.id, direccion: match.direccion || prev.direccion }));
      }
    })
    .catch(err => console.error('Error buscando cliente del presupuesto:', err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


  return (
    <Container maxWidth="lg" sx={{ my: 4 }}>
      <Typography
        variant="h5"
        component="h3"
        className="titulo-app"
        sx={{ textAlign: 'center', width: 'fit-content', mx: 'auto', mb: 4 }}
      >
        Nueva Orden de Servicio
      </Typography>

      <OrdenDatos form={form} onChange={handleChange} />
      <ClienteDatos form={form} setForm={setForm} />
      <VehiculoDatos form={form} setForm={setForm} />
      <DescripcionFalla form={form} onChange={handleChange} />

      <RecepcionVehiculo
        form={form}
        onChange={handleChange}
        onCheck={handleRecepcionCheck}
        onMarkerChange={handleMarkerChange}
        esquemaRef={esquemaRef}
      />
      <div className="card card-app mb-4">
            <div className="card-header-app"><FaDollarSign /> ABONO RECIBIDO</div>
            <div className="card-body">
              <TextField
                label="Valor abonado por el cliente ($)"
                type="number"
                name="abono"
                inputProps={{ min: 0 }}
                value={form.abono}
                onChange={handleChange}
                fullWidth
                helperText="Registra aquí el valor que el cliente entrega como anticipo al recibir el vehículo."
              />
            </div>
          </div>
      <AutorizacionFirma form={form} setForm={setForm} sigPadRef={sigPadRef} />

      {emailAdvertencia && (
        <Alert severity="warning" sx={{ mt: 4 }}>
          <strong>Orden guardada correctamente.</strong><br />
          {emailAdvertencia}
          <Box sx={{ mt: 1 }}>
            <Button size="small" variant="outlined" color="warning" onClick={() => navigate('/ordenes')}>
              Ir a órdenes
            </Button>
          </Box>
        </Alert>
      )}

      {formErrors && (
        <Alert severity="error" sx={{ mt: 4 }}>
          <strong>No se pudo guardar la orden. Corrija los siguientes campos:</strong>
          <ul style={{ marginTop: 8, marginBottom: 0 }}>
            {formErrors.map((msg, i) => <li key={i}>{msg}</li>)}
          </ul>
        </Alert>
      )}

      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ minWidth: 160 }}
        >
          {loading ? 'Guardando...' : 'GUARDAR ORDEN'}
        </Button>
      </Box>
    </Container>
  );
};

export default NuevaOrden;